'use client';

/**
 * DesignVariantsProvider — fetcha config design da /api/design-settings
 * al mount e la espone via useDesignVariants() hook ai componenti.
 *
 * Fallback: DEFAULT_DESIGN_VARIANTS se fetch fallisce.
 * Cache: sessionStorage per evitare re-fetch a ogni route change.
 */

import { createContext, useContext, useEffect, useState } from 'react';
import { DEFAULT_DESIGN_VARIANTS, type DesignVariants, mergeDesignVariants } from '@/lib/design-variants';

const SS_KEY = 'design_variants_v1';

const DesignVariantsContext = createContext<DesignVariants>(DEFAULT_DESIGN_VARIANTS);

export function useDesignVariants(): DesignVariants {
  return useContext(DesignVariantsContext);
}

export default function DesignVariantsProvider({ children }: { children: React.ReactNode }) {
  // Preview override: se URL contiene ?__designPreview=<base64-json>, bypassa
  // fetch e sessionStorage. Usato dall'iframe live preview in /admin/design
  // per visualizzare varianti pending senza dover salvare in DB.
  const previewOverride = (() => {
    if (typeof window === 'undefined') return null;
    try {
      const p = new URLSearchParams(window.location.search).get('__designPreview');
      if (!p) return null;
      return mergeDesignVariants(JSON.parse(atob(p)));
    } catch { return null; }
  })();

  const [variants, setVariants] = useState<DesignVariants>(() => {
    if (previewOverride) return previewOverride;
    // SSR safe: ritorna defaults sempre on server; on client legge sessionStorage
    if (typeof window === 'undefined') return DEFAULT_DESIGN_VARIANTS;
    try {
      const cached = sessionStorage.getItem(SS_KEY);
      if (cached) return mergeDesignVariants(JSON.parse(cached));
    } catch { /* ignore */ }
    return DEFAULT_DESIGN_VARIANTS;
  });

  useEffect(() => {
    if (previewOverride) return; // skip fetch in preview mode
    let cancelled = false;
    fetch('/api/design-settings', { cache: 'no-store' })
      .then((r) => r.json())
      .then((j) => {
        if (cancelled || !j?.success || !j?.data) return;
        const merged = mergeDesignVariants(j.data);
        setVariants(merged);
        try { sessionStorage.setItem(SS_KEY, JSON.stringify(merged)); } catch { /* ignore */ }
      })
      .catch(() => { /* keep current */ });
    return () => { cancelled = true; };
  }, [previewOverride]);

  // Sync font primario → CSS variable su <body>. Le 3 famiglie sono già caricate
  // via next/font in layout.tsx (--font-manrope/--font-jakarta/--font-outfit).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const fontVar =
      variants.font_primary === 'jakarta' ? 'var(--font-jakarta)'
      : variants.font_primary === 'outfit'  ? 'var(--font-outfit)'
      : 'var(--font-manrope)';
    document.body.style.setProperty('--font-primary', fontVar);
    document.body.style.fontFamily = `${fontVar}, sans-serif`;
  }, [variants.font_primary]);

  return (
    <DesignVariantsContext.Provider value={variants}>
      {children}
    </DesignVariantsContext.Provider>
  );
}
